import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext();

// Synchronously get initial dark mode before any rendering
const getInitialDarkMode = () => {
  // Try to get the stored preference first
  const storedDarkMode = localStorage.getItem('darkMode');
  
  if (storedDarkMode !== null) {
    // If there's a stored preference, use it
    return storedDarkMode === 'true';
  }
  
  // If no stored preference, check system preference
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  
  // Default to light mode if all else fails
  return false;
};

// Initialize dark mode immediately
const initialDarkMode = getInitialDarkMode();
// Apply dark mode class immediately to prevent flash
if (initialDarkMode) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}

export const languages = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  bg: "Български",
  ro: "Română"
};

export function AppProvider({ children }) {
  const [darkMode, setDarkMode] = useState(initialDarkMode);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  // Effect for handling dark mode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Listen for system color scheme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if there's no stored preference
      if (localStorage.getItem('darkMode') === null) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prevDarkMode => {
      const newDarkMode = !prevDarkMode;
      localStorage.setItem('darkMode', newDarkMode);
      return newDarkMode;
    });
  }, []);

  const changeLanguage = useCallback((lang) => {
    if (languages[lang]) {
      setCurrentLanguage(lang);
      localStorage.setItem('language', lang);
    }
  }, []);

  const value = {
    darkMode,
    toggleDarkMode,
    currentLanguage,
    changeLanguage,
    languages
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}